import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Beaker, Cog, FlaskConical, Zap, Search, CheckCircle2, ChevronRight } from "lucide-react";
import LitLabLayout from "../components/LitLabLayout";
import { litlabSite, litlabIndustries, litlabAboutTabsGet } from "../api";

const SERVICE_ICONS = {
  metallurgical: Beaker,
  mechanical: Cog,
  chemical: FlaskConical,
  "welder-qualification": Zap,
  forensic: Search,
};

export function LitLabHome() {
  const [site, setSite] = useState(null);
  const [inds, setInds] = useState([]);
  useEffect(() => {
    document.title = "L.I.T. Labs — Metallurgical Testing since 1985";
    Promise.all([litlabSite(), litlabIndustries()]).then(([s, i]) => {
      setSite(s); setInds(i.items || []);
    });
  }, []);

  if (!site) return <LitLabLayout><div className="pt-20 text-center text-slate-500">Loading…</div></LitLabLayout>;

  return (
    <LitLabLayout>
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-20 bg-gradient-to-b from-sky-50 via-white to-sky-50" data-testid="litlab-hero">
        <div className="absolute inset-0 litlab-grid-bg opacity-70 pointer-events-none" />
        <div className="absolute -right-40 top-20 w-[500px] h-[500px] rounded-full bg-sky-200/50 blur-3xl pointer-events-none" />
        <div className="absolute -left-40 bottom-0 w-[400px] h-[400px] rounded-full bg-amber-100/60 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-emerald-700 font-bold border border-emerald-400/40 bg-emerald-50 rounded-full px-3 py-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Nadcap accredited · Since 1985
            </div>
            <h1 className="mt-8 litlab-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight text-slate-900 max-w-4xl">
              Aerospace-grade <span className="text-sky-700">metallurgical testing</span>. Documented to survive any audit.
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-2xl">
              {site.site.tagline}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/litlab/quote" data-testid="litlab-hero-quote"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white px-6 py-3.5 rounded font-bold transition shadow-sm">
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/litlab/services" data-testid="litlab-hero-services"
                className="inline-flex items-center gap-2 border border-sky-300 bg-white hover:border-sky-500 text-slate-700 hover:text-slate-900 px-6 py-3.5 rounded font-semibold transition">
                Browse capabilities
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-2 gap-3">
              {site.site.hero_bullets.map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`litlab-hero-bullet-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> {b}
                </div>
              ))}
            </div>
          </div>
          {/* Lab photography panel — REAL photos pulled directly from
              litlab.com's own asset library, so the site stays visually
              on-brand with the client's own imagery. */}
          <div className="lg:col-span-2 relative">
            <div className="relative rounded-2xl overflow-hidden border border-sky-200 shadow-lg bg-slate-50 aspect-[4/3] sm:aspect-[16/10]">
              <img src="https://www.litlab.com/files/slides/4053127774.JPG"
                    alt="L.I.T. Labs metallurgical testing lab"
                    className="w-full h-full object-contain" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">On the lab floor</div>
                <div className="mt-1 text-sm font-semibold text-white">Metallography · Photomicrography · Failure analysis</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-sky-200 bg-slate-50 aspect-[4/3] overflow-hidden">
                <img src="https://www.litlab.com/files/slides/1177685004.JPG"
                      alt="Chemical analysis at L.I.T. Labs"
                      className="w-full h-full object-contain" />
              </div>
              <div className="rounded-lg border border-sky-200 bg-slate-50 aspect-[4/3] overflow-hidden">
                <img src="https://www.litlab.com/files/slides/7482738499.JPG"
                      alt="Nadcap accredited materials testing"
                      className="w-full h-full object-contain" />
              </div>
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-400 text-center">
              Photography from L.I.T. Labs · Hauppauge, NY
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges strip */}
      <section className="bg-white border-y border-sky-200 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-6">
          <div className="text-xs uppercase tracking-[0.3em] text-slate-500 font-bold">Accredited by</div>
          <div className="flex flex-wrap items-center gap-4">
            {site.site.accreditations.map((a) => (
              <div key={a.key} data-testid={`litlab-acc-${a.key}`}
                className="rounded-lg border border-emerald-500/40 bg-emerald-50 px-4 py-2">
                <div className="text-sm font-bold text-emerald-700">{a.label}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{a.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-sky-50" id="services">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">What we do</div>
          <h2 className="mt-2 litlab-serif text-3xl md:text-4xl font-bold text-slate-900 max-w-3xl">Five testing disciplines. One accredited lab.</h2>
          <p className="mt-3 text-slate-600 max-w-2xl">Every test methodology is documented, calibrated and traceable — the way aerospace and defense customers require.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {site.services.map((s) => {
              const Icon = SERVICE_ICONS[s.slug] || Beaker;
              return (
                <Link key={s.slug} to={`/litlab/services#${s.slug}`}
                  data-testid={`litlab-service-${s.slug}`}
                  className="group relative rounded-xl border border-sky-200 bg-white hover:border-sky-500 hover:shadow-md p-6 transition">
                  <div className="w-11 h-11 rounded-lg bg-sky-100 border border-sky-300 grid place-items-center">
                    <Icon className="w-5 h-5 text-sky-700" />
                  </div>
                  <div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-bold">{s.kicker}</div>
                  <div className="mt-1 text-lg font-bold text-slate-900">{s.label}</div>
                  <div className="mt-2 text-sm text-slate-600 line-clamp-3">{s.description}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-sky-700 group-hover:gap-2 transition-all">
                    See tests <ChevronRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 border-t border-sky-200 bg-white relative">
        <div className="absolute inset-0 litlab-diagonal pointer-events-none opacity-70" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Industries we serve</div>
          <h2 className="mt-2 litlab-serif text-3xl md:text-4xl font-bold text-slate-900 max-w-3xl">From turbine blades to structural welds.</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {inds.map((i) => (
              <Link key={i.slug} to={`/litlab/industries/${i.slug}`}
                data-testid={`litlab-industry-tile-${i.slug}`}
                className="rounded-lg border border-sky-200 hover:border-sky-500 bg-white hover:shadow-sm p-4 transition group">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{i.kicker}</div>
                <div className="mt-1 text-sm font-bold text-slate-900 group-hover:text-sky-700 transition">{i.label}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-sky-700 via-sky-600 to-sky-800 border-t border-sky-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="litlab-serif text-3xl md:text-5xl font-bold">Have a sample? Have a deadline?</h2>
          <p className="mt-3 text-sky-100 text-lg">We quote most standard tests the same business day — and rush turnarounds are our specialty.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/litlab/quote" data-testid="litlab-cta-quote"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white px-6 py-3.5 rounded font-bold transition shadow-sm">
              Request a quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:631-643-6792" data-testid="litlab-cta-call"
              className="inline-flex items-center gap-2 border border-white/40 hover:border-white bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded font-semibold transition">
              Call 631-643-6792
            </a>
          </div>
        </div>
      </section>
    </LitLabLayout>
  );
}

export function LitLabAbout() {
  const [site, setSite] = useState(null);
  const [tabs, setTabs] = useState(null);
  useEffect(() => {
    document.title = "About L.I.T. Labs";
    litlabSite().then(setSite);
    litlabAboutTabsGet().then((d) => setTabs(d.tabs || [])).catch(() => {});
  }, []);
  if (!site) return <LitLabLayout><div className="pt-20 text-center text-slate-500">Loading…</div></LitLabLayout>;
  const brand = site.site.brand;
  const displayTabs = tabs && tabs.length ? tabs : [
    { key: "since", label: "Established", value: brand.since, blurb: "" },
    { key: "discipline", label: "Discipline", value: "Metallurgy", blurb: "chemical · mechanical · forensic" },
    { key: "accreditation", label: "Accreditation", value: "Nadcap", blurb: "Materials Testing" },
    { key: "standard", label: "Standard", value: "AC7006", blurb: "≡ ISO/IEC 17025" },
  ];
  return (
    <LitLabLayout>
      <section className="pt-20 pb-16 bg-white border-b border-sky-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Who we are</div>
          <h1 className="mt-3 litlab-serif text-4xl md:text-6xl font-bold text-slate-900">Four decades of metallurgical discipline.</h1>
          <p className="mt-6 text-lg text-slate-700 leading-relaxed">
            L.I.T. Labs, Inc. — formerly Long Island Testing Laboratories, Inc. — has been the
            independent metallurgical lab of choice for aerospace, defense and manufacturing
            customers on Long Island and beyond since {brand.since}.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            We are <strong className="text-emerald-700">Nadcap accredited for Materials Testing</strong> and
            hold <strong className="text-emerald-700">AC7006</strong>, the aerospace-industry equivalent of ISO/IEC 17025.
            Every test procedure, calibration, and record we keep is auditable against that standard.
          </p>
        </div>
      </section>

      <section className="py-20 bg-sky-50">
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6"
             data-testid="litlab-about-tabs">
          {displayTabs.map((t) => (
            <StatCard key={t.key} label={t.label} value={t.value} suffix={t.blurb} />
          ))}
        </div>
      </section>

      <section className="py-16 bg-white border-t border-sky-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">What guides our work</div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Pillar title="Traceability" body="Every sample is barcoded and tracked from receiving through disposal. Every test has a signed procedure, a calibrated instrument, and a documented result." />
            <Pillar title="Speed" body="Rush turnaround is not an exception — it's a discipline. We schedule mechanical loads, met-lab benches and OES chemistry so most standard tests quote same-day." />
            <Pillar title="Honesty" body="If a test can't be run to spec, we say so — before the invoice, not after. If a customer's part fails, we tell them why, not just that." />
          </div>
        </div>
      </section>
    </LitLabLayout>
  );
}

function StatCard({ label, value, suffix }) {
  return (
    <div className="rounded-xl border border-sky-200 bg-white p-6 shadow-sm" data-testid={`litlab-stat-${label.toLowerCase()}`}>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="mt-2 litlab-serif text-4xl font-bold text-sky-700">{value}</div>
      {suffix && <div className="mt-1 text-sm text-slate-500">{suffix}</div>}
    </div>
  );
}
function Pillar({ title, body }) {
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 p-5">
      <div className="litlab-serif text-xl font-bold text-slate-900">{title}</div>
      <div className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}
